<script setup lang="ts">
import PdsIcon from '@/components/pds/PdsIcon.vue';

// Heure « HH:MM » ; vide = pas de borne. Un sélecteur fait pour le doigt : on avance et on recule par demi-heures,
// sans passer par le clavier ni par le sélecteur du système.
const props = defineProps<{ label: string; startAt: string }>();
const time = defineModel<string>({ required: true });

const STEP_MINUTES = 30;
const LAST = 24 * 60 - STEP_MINUTES;

const toMinutes = (value: string) => {
  const [hours, minutes] = value.split(':').map(Number);
  return hours * 60 + minutes;
};
const toText = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

// Le premier toucher sur un champ vide le pose à `startAt` ; ensuite chaque toucher avance d'un cran, sans sortir de la journée.
const move = (direction: 1 | -1) => {
  if (!time.value) {
    time.value = props.startAt;
    return;
  }
  const next = toMinutes(time.value) + direction * STEP_MINUTES;
  time.value = toText(Math.min(LAST, Math.max(0, next)));
};

const button =
  'bg-second text-encre flex size-12 shrink-0 cursor-pointer items-center justify-center rounded-full';
</script>

<template>
  <div class="flex min-w-0 flex-1 flex-col gap-1">
    <span class="text-encre-douce text-[12px] font-semibold">{{ label }}</span>
    <div
      class="bg-carte flex h-14 items-center gap-1 rounded-full px-1 shadow-[inset_0_0_0_1px_var(--pds-filet-fort)]"
    >
      <button
        type="button"
        :class="button"
        :aria-label="`${label} : une demi-heure plus tôt`"
        @click="move(-1)"
      >
        <PdsIcon name="minus" :size="20" />
      </button>
      <span
        class="min-w-0 flex-1 text-center text-[18px] leading-6 font-semibold"
        :class="!time && 'text-encre-douce'"
        aria-live="polite"
      >
        {{ time || '—' }}
      </span>
      <button
        v-if="time"
        type="button"
        class="text-encre-douce flex size-8 shrink-0 cursor-pointer items-center justify-center"
        :aria-label="`${label} : retirer la borne`"
        @click="time = ''"
      >
        <PdsIcon name="close" :size="16" />
      </button>
      <button
        type="button"
        :class="button"
        :aria-label="`${label} : une demi-heure plus tard`"
        @click="move(1)"
      >
        <PdsIcon name="plus" :size="20" />
      </button>
    </div>
  </div>
</template>
